import { useLazyQuery, useMutation } from '@apollo/client'
import {
    IsBestPracticeOutOfSyncForUI,
    UpdateBestBestPracticeVersion,
} from 'api/graphQL/mutations/bestPractices.mutations'
import { UpdateBestPracticeElements } from 'api/graphQL/mutations/projectProductFamily.mutations'
import { BestPracticeQuery } from 'api/graphQL/queries/bestPractices.queries'
import { useShallowEditorActionBar } from 'components/Workspace2EditorActionBar/store/EditorActionBarStore'
import {
    useContentItemFetcher,
    usePermissionChecks,
} from 'components/WorkspaceEditor/hooks'
import { getActiveTabLabel } from 'components/WorkspaceEditor/utils'
import { getParentArticleNode } from 'components/WorkspaceEditor/utils/hoveredActionUtils'
import {
    BestPracticeDataExtended,
    Elements,
} from 'pages/ProjectsPage/WorkspacePage/components/Common/BestPracticeSyncTypes'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useParams } from 'react-router-dom'
import {
    useShallowSelector,
    useWorkspace2Store,
} from '../../../Workspace2Store'
import {
    BestPracticeSyncAction,
    SyncActionResult,
} from '../../smartProjectCopy/types'
import { BestPracticeContentItem } from './useBestPracticesData'

// Constants
// Debounce sync checks by 200ms to prevent excessive API calls when content
// is rapidly edited or multiple state updates occur in quick succession
const SYNC_CHECK_DEBOUNCE_MS = 200

interface BestPracticeData {
    id: string
    currentVersionId?: string
    isUserAddedContentBestPractice?: boolean
}

export interface UseBestPracticesSyncStatusProps {
    contentItem: BestPracticeContentItem
    projectElementNodeId?: string
    enabled?: boolean
}

export interface UseBestPracticesSyncStatusReturn {
    syncActionType: SyncActionResult
    isBPOutOfSync: boolean
    isGBPOutOfSync: boolean
    isLoading: boolean
    checkSyncStatus: () => void
    handleSync: () => void
    /** Whether SyncFromBP action is available (workspace context provided) */
    isSyncFromBPEnabled: boolean
}

/**
 * Hook to check and manage Best Practice sync status.
 * Determines if a content item's BP is out of sync and provides handlers for syncing.
 *
 * @param contentItem - The content item to check sync status for
 * @param projectElementNodeId - The project element node ID
 * @param enabled - Whether to automatically check sync status (default: true)
 *
 * @example
 * ```typescript
 * const { syncActionType, isBPOutOfSync, checkSyncStatus } = useBestPracticesSyncStatus({
 *     contentItem: node,
 *     projectElementNodeId: node.id,
 *     enabled: true
 * })
 * ```
 */
export const useBestPracticesSyncStatus = ({
    contentItem,
    projectElementNodeId,
    enabled = true,
}: UseBestPracticesSyncStatusProps): UseBestPracticesSyncStatusReturn => {
    const { projectId } = useParams<{ projectId: string }>()
    const contentItemViewNodes = useWorkspace2Store(
        (state) => state.contentItemViewNodes
    )
    const areTrackChangesVisible = useShallowEditorActionBar(
        (state) => state.visibilitySettingValues.areTrackChangesVisible
    )
    const {
        selectedProjectElementNode,
        activeTab,
        treeData,
        setTreeData,
        actions: { triggerBPSyncCheck },
    } = useShallowSelector((state) => ({
        selectedProjectElementNode: state.selectedProjectElementNode,
        activeTab: state.activeTab,
        treeData: state.treeData,
        setTreeData: state.actions.setTreeData,
        actions: state.actions,
    }))
    const permission = usePermissionChecks(treeData, selectedProjectElementNode)
    const { fetchContentItemView } = useContentItemFetcher({
        projectId: projectId!,
        treeData,
        setTreeData,
        permission,
    })
    const productFamilyId = useMemo(
        () => selectedProjectElementNode?.elementId,
        [selectedProjectElementNode?.elementId]
    )
    // Watch for BP sync trigger (incremented after content saves)
    const bpSyncTrigger = useWorkspace2Store((state) => state.bpSyncTrigger)
    const [syncActionType, setSyncActionType] = useState<SyncActionResult>({
        action: BestPracticeSyncAction.Null,
        currentVersionId: '',
        isUserAddedContentBestPractice: false,
        latestVersionId: '',
        message: '',
    })

    const contentItemId =
        contentItem.contentItemView?.id || contentItem.id || ''

    const isBestPractice =
        contentItem.isBestPractice ??
        contentItem.contentItemView?.isBestPractice ??
        false

    const isGlobalBestPractice =
        contentItem.isGlobalBestPractice ??
        contentItem.contentItemView?.isGlobalBestPractice ??
        false

    const isHidden =
        contentItem.isHidden ?? contentItem.contentItemView?.isHidden ?? false

    // Ref to track if a sync check is already in progress to prevent duplicate calls
    const syncCheckInProgressRef = useRef(false)
    // Ref to track the last time a sync check was performed (for debouncing)
    const lastSyncCheckTimeRef = useRef<number>(0)
    // Ref to track the last sync action to prevent unnecessary state updates
    const lastSyncActionRef = useRef<BestPracticeSyncAction>(
        BestPracticeSyncAction.Null
    )

    // Cleanup refs on unmount to prevent memory leaks
    useEffect(() => {
        return () => {
            syncCheckInProgressRef.current = false
            lastSyncCheckTimeRef.current = 0
        }
    }, [])

    /**
     * Gets the latest content item elements directly from the store.
     *
     * Uses getState() to read current state on-demand rather than from props/closures.
     * This ensures we always have the latest elements, even when called from async
     * callbacks or closures that may execute after state updates.
     *
     * Not wrapped in useCallback since the function is lightweight and has no dependencies
     * that would benefit from memoization.
     *
     * @returns Array of content item elements with their current state
     */
    const getContentItemElements = (): Partial<Elements>[] => {
        // Always read fresh data directly from the store (not captured in closure)
        const freshContentItemViewNodes = useWorkspace2Store.getState()
            .contentItemViewNodes
        const freshNode = freshContentItemViewNodes.find(
            (node) => node.id === contentItemId
        )
        const elements =
            freshNode?.elements ||
            contentItem.contentItemView?.elements ||
            (contentItem as BestPracticeContentItem & {
                elements?: Partial<Elements>[]
            }).elements ||
            []

        if (!elements || elements.length === 0) {
            return []
        }

        return elements.map((element: Partial<Elements>) => ({
            id: element.id,
            imperialUnits: element.imperialUnits,
            metricUnits: element.metricUnits,
            optionItemState: element.optionItemState,
            text: element.text,
            type: element.type,
            placeholderText: element.placeholderText,
            changeType: element.changeType,
            authoredUserId: element.authoredUserId,
            elements: element.elements || [],
        }))
    }

    // Query to get Best Practice data
    const [
        getBestPracticeData,
        { data: bestPracticeData, loading: bpLoading },
    ] = useLazyQuery(BestPracticeQuery, {
        fetchPolicy: 'network-only',
        onCompleted: () => {
            // Reset the in-progress flag when BP data fetch completes
            syncCheckInProgressRef.current = false
        },
        onError: (error) => {
            // Reset the in-progress flag on error
            syncCheckInProgressRef.current = false
        },
    })

    // Mutation to check if BP is out of sync
    const [
        checkIsBestPracticeOutOfSyncForUI,
        { loading: syncCheckLoading },
    ] = useMutation<
        { isBestPracticeOutOfSyncForUI: SyncActionResult },
        { input: Partial<BestPracticeDataExtended> }
    >(IsBestPracticeOutOfSyncForUI, {
        onCompleted: (data) => {
            // Reset the in-progress flag when mutation completes
            syncCheckInProgressRef.current = false
            if (data?.isBestPracticeOutOfSyncForUI) {
                const newAction = data.isBestPracticeOutOfSyncForUI.action

                // Only update state if the sync action has actually changed
                if (newAction !== lastSyncActionRef.current) {
                    lastSyncActionRef.current = newAction
                    setSyncActionType(data.isBestPracticeOutOfSyncForUI)
                }
            }
        },
        onError: (error) => {
            // Reset the in-progress flag on error
            syncCheckInProgressRef.current = false

            // On error, preserve the previous sync action state
            // Keep the same state so the icon remains stable
        },
    })

    // Mutation to update BP version with current content (CreateNewBP action)
    const [
        updateBestPracticeVersion,
        { loading: updateBPLoading },
    ] = useMutation(UpdateBestBestPracticeVersion, {
        onCompleted: () => {
            // After successful sync, optimistically set to "synced" state
            lastSyncActionRef.current = BestPracticeSyncAction.None
            setSyncActionType({
                action: BestPracticeSyncAction.None,
                currentVersionId: '',
                isUserAddedContentBestPractice: false,
                latestVersionId: '',
                message: 'Synced',
            })
        },
        onError: (error) => {
            console.error('[BP Sync] Error updating BP version:', {
                error: error.message,
                contentItemId,
                projectId,
                bpId: bestPracticeData?.getBestPractice?.id,
                timestamp: new Date().toISOString(),
                stack: error.stack,
            })
        },
    })

    // Mutation to update content from BP (SyncFromBP action)
    const [
        updateContentFromBestPractice,
        { loading: syncFromBPLoading },
    ] = useMutation(UpdateBestPracticeElements(6), {
        fetchPolicy: 'no-cache',
        onCompleted: () => {
            // After successful sync, optimistically set to "synced" state
            lastSyncActionRef.current = BestPracticeSyncAction.None
            setSyncActionType({
                action: BestPracticeSyncAction.None,
                currentVersionId: '',
                isUserAddedContentBestPractice: false,
                latestVersionId: '',
                message: 'Synced',
            })
        },
        onError: (error) => {
            console.error('[BP Sync] Error syncing content from BP:', {
                error: error.message,
                contentItemId,
                projectId,
                productFamilyId,
                timestamp: new Date().toISOString(),
                stack: error.stack,
            })
        },
    })

    // Check if SyncFromBP is enabled (workspace context is provided)
    const isSyncFromBPEnabled = useMemo(() => {
        return !!productFamilyId
    }, [productFamilyId])

    // Get content item node from contentItemViewNodes
    const contentItemNode = useMemo(() => {
        return contentItemViewNodes.find((node) => node.id === contentItemId)
    }, [contentItemViewNodes, contentItemId])

    // Find article content item ID from content item view nodes
    // If the content item is an article, use its ID; otherwise find its parent article
    const getArticleContentItemId = useCallback((): string => {
        if (!contentItemNode) return contentItemId

        // If this is an article, use its ID
        if (contentItemNode.textType === 'Article') {
            return contentItemId
        }

        // Find parent article by traversing up through dtvContentItemParentId
        const parentId = contentItemNode.dtvContentItemParentId
        if (parentId) {
            const parentNode = contentItemViewNodes.find(
                (node) => node.id === parentId
            )
            if (parentNode?.textType === 'Article') {
                return parentId
            }
        }

        // Fallback to content item ID
        return contentItemId
    }, [contentItemNode, contentItemId, contentItemViewNodes])

    // Build editing input for SyncFromBP mutation
    const buildEditingInput = useCallback(() => {
        if (!contentItemNode) return null

        return {
            projectProductFamilyId: productFamilyId || '',
            contentItemId: contentItemId,
            contentItemOwnerType:
                contentItemNode.contentItemOwnerType || 'Nugget',
            elements: [], // Empty elements for SyncFromBP - server replaces with BP content
            projectId: projectId || '',
            contentItemOwnerId: contentItemNode.dtvItemId || '',
            nodeId: projectElementNodeId || '',
            textType: contentItemNode.textType || 'Article',
            partType: contentItemNode.partType || 'Part',
            documentTemplateViewId:
                contentItemNode.documentTemplateViewId || '',
            documentTemplateViewItemId: contentItemNode.dtvItemId || '',
            // Double fallback: dtvContentItemParentId (from content item API) or dtvParentId (from tree structure API)
            contentItemParentId:
                contentItemNode.dtvContentItemParentId ||
                contentItemNode.dtvParentId ||
                '',
            contentItemParentType:
                contentItemNode.contentItemParentType || 'None',
            propertyKey: contentItemNode.propertyRefId,
            isFromStudioManagementModule: false,
            isMasterSpecCloneNode: false,
            selectedPillElementId: '',
            isDeletingWholeParagraph: false,
            isDeletingBaseContent: false,
        }
    }, [
        contentItemNode,
        contentItemId,
        projectId,
        projectElementNodeId,
        productFamilyId,
    ])

    // Check sync status
    const checkSyncStatus = useCallback(() => {
        if (
            !projectId ||
            !contentItemId ||
            (!isBestPractice && !isGlobalBestPractice)
        ) {
            return
        }

        // Prevent duplicate calls if a check is already in progress
        if (syncCheckInProgressRef.current) {
            return
        }

        // Debounce: Prevent calls that happen too rapidly
        const now = Date.now()
        if (now - lastSyncCheckTimeRef.current < SYNC_CHECK_DEBOUNCE_MS) {
            return
        }

        // Mark as in progress BEFORE starting operation
        syncCheckInProgressRef.current = true
        // Update timestamp when operation starts (not before validation)
        lastSyncCheckTimeRef.current = now

        // First get BP data if we don't have it
        if (!bestPracticeData?.getBestPractice) {
            // Note: onCompleted/onError callbacks will reset the flag
            getBestPracticeData({
                variables: {
                    parentId: contentItemId,
                    projectId: projectId,
                },
            })
        } else {
            // We have BP data, check sync status
            const bpData: BestPracticeData = bestPracticeData.getBestPractice

            const mutationInput: Partial<BestPracticeDataExtended> = {
                parentProjectId: projectId,
                contentItemId: contentItemId,
                id: bpData.id,
                projectElementNodeId: projectElementNodeId,
                elements: getContentItemElements(),
                currentVersionId: bpData.currentVersionId,
                isHidden: isHidden,
                isUserAddedContentBestPractice:
                    bpData.isUserAddedContentBestPractice,
            }

            // Note: onCompleted/onError callbacks will reset the flag
            checkIsBestPracticeOutOfSyncForUI({
                variables: {
                    input: mutationInput,
                },
            })
        }
    }, [
        projectId,
        contentItemId,
        isBestPractice,
        isGlobalBestPractice,
        projectElementNodeId,
        bestPracticeData,
        getBestPracticeData,
        checkIsBestPracticeOutOfSyncForUI,
        isHidden,
        contentItemNode,
    ])

    // Auto-fetch BP data when component mounts and is enabled
    useEffect(() => {
        if (
            enabled &&
            projectId &&
            contentItemId &&
            (isBestPractice || isGlobalBestPractice)
        ) {
            // Fetch BP data which will trigger onCompleted -> checkSyncStatus
            getBestPracticeData({
                variables: {
                    parentId: contentItemId,
                    projectId: projectId,
                },
            })
        }
    }, [
        enabled,
        projectId,
        contentItemId,
        isBestPractice,
        isGlobalBestPractice,
        getBestPracticeData,
    ])

    // Auto-check sync status when BP data is loaded
    useEffect(() => {
        if (
            enabled &&
            bestPracticeData?.getBestPractice &&
            (isBestPractice || isGlobalBestPractice)
        ) {
            checkSyncStatus()
        }
    }, [
        enabled,
        bestPracticeData,
        isBestPractice,
        isGlobalBestPractice,
        checkSyncStatus,
    ])

    // Re-check sync status when content is saved (bpSyncTrigger changes)
    useEffect(() => {
        // Only trigger on actual changes (not initial mount when bpSyncTrigger is 0)
        if (
            bpSyncTrigger &&
            bpSyncTrigger > 0 &&
            enabled &&
            (isBestPractice || isGlobalBestPractice)
        ) {
            // Call checkSyncStatus which will fetch BP data if needed
            // or check sync status if data already exists
            checkSyncStatus()
        }
    }, [
        bpSyncTrigger,
        enabled,
        isBestPractice,
        isGlobalBestPractice,
        checkSyncStatus,
    ])

    // Handle sync icon click - executes sync based on action type
    const handleSync = useCallback(async () => {
        if (
            !bestPracticeData?.getBestPractice ||
            !projectId ||
            !contentItemId
        ) {
            return
        }

        const bpData: BestPracticeData = bestPracticeData.getBestPractice
        const action = syncActionType.action

        // Only handle CreateNewBP action for now
        // SyncFromBP requires more workspace context (editingInput, etc.)
        if (action === BestPracticeSyncAction.CreateNewBP) {
            const mutationInput: Partial<BestPracticeDataExtended> = {
                parentProjectId: projectId,
                contentItemId: contentItemId,
                id: bpData.id,
                projectElementNodeId: projectElementNodeId,
                elements: getContentItemElements(),
                currentVersionId: bpData.currentVersionId,
                isHidden: isHidden,
                isUserAddedContentBestPractice:
                    bpData.isUserAddedContentBestPractice,
            }

            updateBestPracticeVersion({
                variables: {
                    input: mutationInput,
                },
            })
        } else if (action === BestPracticeSyncAction.SyncFromBP) {
            // SyncFromBP requires workspace context to be provided
            if (!isSyncFromBPEnabled) {
                return
            }

            const editingInput = buildEditingInput()
            if (!editingInput) {
                return
            }

            // Build best practice input
            const bestPracticeInput: Partial<BestPracticeDataExtended> = {
                parentProjectId: projectId,
                contentItemId: contentItemId,
                id: bpData.id,
                projectElementNodeId: projectElementNodeId,
                elements: getContentItemElements(),
                currentVersionId: bpData.currentVersionId,
                isHidden: isHidden,
                isUserAddedContentBestPractice:
                    bpData.isUserAddedContentBestPractice,
            }

            // Get article ID for rootArticleContentItemIds
            if (!contentItemNode) {
                return
            }

            const parentArticleId =
                getParentArticleNode(contentItemNode, contentItemViewNodes)
                    ?.id ?? ''

            try {
                await updateContentFromBestPractice({
                    variables: {
                        input: editingInput,
                        partType: String(editingInput.partType).toUpperCase(),
                        projectId: projectId,
                        rootArticleContentItemIds: [parentArticleId],
                        bestPracticeInput: bestPracticeInput,
                        productFamilyId: productFamilyId,
                        isTrackChangeToggleOn: areTrackChangesVisible ?? false,
                        isResetAllOptions: false,
                    },
                })

                await fetchContentItemView(getActiveTabLabel(activeTab), [
                    parentArticleId.toString(),
                ])

                triggerBPSyncCheck?.()
            } catch (error) {
                // On error, preserve the current state - don't check sync status
            }
        }
    }, [
        bestPracticeData,
        projectId,
        contentItemId,
        projectElementNodeId,
        syncActionType.action,
        isHidden,
        getContentItemElements,
        updateBestPracticeVersion,
        checkSyncStatus,
        isSyncFromBPEnabled,
        areTrackChangesVisible,
        buildEditingInput,
        contentItemNode,
        updateContentFromBestPractice,
        getArticleContentItemId,
        productFamilyId,
        triggerBPSyncCheck,
        activeTab,
    ])

    const isBPOutOfSync =
        isBestPractice &&
        (syncActionType.action === BestPracticeSyncAction.SyncFromBP ||
            syncActionType.action === BestPracticeSyncAction.CreateNewBP)

    const isGBPOutOfSync =
        isGlobalBestPractice &&
        (syncActionType.action === BestPracticeSyncAction.SyncFromBP ||
            syncActionType.action === BestPracticeSyncAction.CreateNewBP)

    return {
        syncActionType,
        isBPOutOfSync,
        isGBPOutOfSync,
        isLoading: bpLoading || updateBPLoading || syncFromBPLoading,
        checkSyncStatus,
        handleSync,
        isSyncFromBPEnabled,
    }
}
